import { db } from '@core/db/db'
import type {
  CorrectionInfo,
  FiscalReceiptData,
  QueuedSale,
  ReceiptStatus,
  SaleReceipt,
  SaleReceiptItem,
  SettlementType
} from '@core/db/models'
import { fiscalService, type FiscalDocResult, type KktState } from '@core/services/fiscal/FiscalService'
import { syncService } from '@core/services/sync/SyncService'
import { validateCorrectionItems, validateCorrectionTotal } from '@core/services/sale/returnValidation'
import { generateUUID } from '@core/utils/crypto'
import { getActiveShiftContext } from '@core/services/shift/activeShiftContext'

/** Позиция чека коррекции (состав для ФФД и журнала). */
export interface CorrectionLine {
  barcode?: string
  name: string
  qty: number
  price: number
  unit?: string
}

/** Входные данные для чека коррекции. */
export interface CreateCorrectionInput {
  /** Признак расчёта (ФФД 1054). */
  settlementType: SettlementType
  /** Реквизиты коррекции (основание/предписание). */
  correction: CorrectionInfo
  total: number
  paymentMethod: 'cash' | 'electronic'
  cashierName?: string
  /**
   * Состав чека коррекции (ARCH-TASK-65.6.2). Может расходиться с суммой —
   * ФФД допускает коррекцию расчёта без номенклатуры (пустой массив).
   */
  items?: CorrectionLine[]
  /**
   * Исходный чек, по которому оформляется коррекция (ARCH-TASK-65.6).
   * Для «возврата прихода» по продаже без фискального чека: лимит по сумме
   * и по позициям, связь в журнале/облаке.
   */
  originalReceiptId?: string
}

/** Результат создания коррекции. */
export interface CreateCorrectionResult {
  ok: boolean
  receiptId: string
  status: ReceiptStatus
  fiscalData?: FiscalReceiptData
  /**
   * Состояние ККТ на момент оформления (ARCH-TASK-65.6.2): нужно, чтобы
   * объяснить, почему коррекция не пробита — нет бумаги/смена закрыта/ошибка.
   */
  kktState?: KktState
  error?: string
}

/** Преобразовать ответ ККТ в локальные фискальные реквизиты чека. */
function toFiscalData(result: FiscalDocResult): FiscalReceiptData {
  return {
    fiscalDocumentNumber: result.fiscalDocumentNumber,
    fiscalDocumentSign: result.fiscalDocumentSign,
    shiftNumber: result.shiftNumber,
    fiscalDriveNumber: result.fiscalDriveNumber,
    receiptNumber: result.receiptNumber,
    dateTime: result.dateTime
  }
}

/**
 * Сервис чеков коррекции (ФЗ-54, ФФД).
 *
 * Отличия от обычной продажи:
 * - документ всегда фискальный по смыслу: non_fiscal не применяется;
 * - если ККТ доступна — печатается сразу (doc_type='correction');
 * - если ККТ недоступна или печать не удалась — коррекция сохраняется
 *   со статусом offline_pending и уходит в очередь дофискализации;
 * - receiptId генерируется один раз и повторно используется при ретраях
 *   (идемпотентность в агенте по uuid).
 */
export class CorrectionService {
  /** Создать и (при возможности) пробить чек коррекции. */
  public async createCorrection(input: CreateCorrectionInput): Promise<CreateCorrectionResult> {
    // Единственный id на весь жизненный цикл документа (в т.ч. для ретраев).
    const receiptId = generateUUID()
    const createdAt = Date.now()

    if (!Number.isFinite(input.total) || input.total <= 0) {
      return {
        ok: false,
        receiptId,
        status: 'draft',
        error: 'Сумма коррекции должна быть больше нуля'
      }
    }
    if (
      !input.correction.basis.trim() ||
      !input.correction.baseDate.trim() ||
      !input.correction.baseNumber.trim()
    ) {
      return {
        ok: false,
        receiptId,
        status: 'draft',
        error: 'Заполните основание, дату и номер документа'
      }
    }
    if (
      input.correction.kind === 'instruction' &&
      (!input.correction.instructionDate?.trim() || !input.correction.instructionNumber?.trim())
    ) {
      return {
        ok: false,
        receiptId,
        status: 'draft',
        error: 'Для коррекции по предписанию укажите дату и номер предписания'
      }
    }

    // ARCH-TASK-65.6: коррекция-возврат по чеку — лимит по СУММЕ.
    const originalReceiptId = input.originalReceiptId?.trim() || undefined
    if (originalReceiptId && input.settlementType === 'income_return') {
      const source = await db.receipts.where('receiptId').equals(originalReceiptId).first()
      if (!source) {
        return { ok: false, receiptId, status: 'draft', error: 'Исходный чек не найден' }
      }
      // Коррекция-возврат имеет смысл ТОЛЬКО по нефискальной продаже: для
      // фискального прихода возврат оформляется обычным sell_return (65.6).
      if ((source.docType ?? 'sale') !== 'sale') {
        return {
          ok: false,
          receiptId,
          status: 'draft',
          error: 'Коррекция-возврат оформляется только по чеку продажи'
        }
      }
      if (source.status !== 'non_fiscal') {
        return {
          ok: false,
          receiptId,
          status: 'draft',
          error: 'Коррекция-возврат оформляется только по продаже без фискального чека'
        }
      }
      const alreadyReturned = await this.correctionReturnsTotal(originalReceiptId)
      const check = validateCorrectionTotal(source.total, alreadyReturned, input.total)
      if (!check.ok) {
        return { ok: false, receiptId, status: 'draft', error: check.reason }
      }
      // Лимит по позициям: состав коррекции не может превышать остаток.
      const requestedItems = (input.items ?? []).filter((i) => i.qty > 0)
      if (requestedItems.length > 0) {
        const returnedItems = await this.correctionReturnedItems(originalReceiptId)
        const itemsCheck = validateCorrectionItems(
          source.items,
          returnedItems,
          requestedItems.map((i) => ({
            barcode: i.barcode ?? '',
            name: i.name,
            price: i.price,
            qty: i.qty
          }))
        )
        if (!itemsCheck.ok) {
          return { ok: false, receiptId, status: 'draft', error: itemsCheck.reason }
        }
      }
    }

    try {
      const deviceId = await syncService.ensureDeviceId()
      // ARCH-TASK-65.6: зона/смена — иначе коррекция не попадёт в облачный Z
      // по зоне и в «Оборот кассы» с фильтром по зоне.
      const shiftContext = await getActiveShiftContext(db)
      // Состав (может быть пустым — коррекция расчёта без номенклатуры).
      const items = (input.items ?? []).filter((i) => i.qty > 0)
      const fiscalItems = items.map((i) => ({ name: i.name, price: i.price, quantity: i.qty }))

      // 1) ККТ в рабочем состоянии — пробуем напечатать сразу.
      if (fiscalService.getKktStatus()?.state === 'ok') {
        try {
          const result = await fiscalService.printFiscalReceipt({
            id: receiptId,
            cashierName: input.cashierName,
            total_amount: input.total,
            paymentMethod: input.paymentMethod,
            items: fiscalItems,
            docType: 'correction',
            settlementType: input.settlementType,
            correction: input.correction
          })
          const fiscalData = toFiscalData(result)
          await this.saveReceipt({
            receiptId,
            total: input.total,
            paymentMethod: input.paymentMethod,
            cashierName: input.cashierName,
            settlementType: input.settlementType,
            correction: input.correction,
            originalReceiptId,
            items,
            zoneId: shiftContext.zoneId,
            shiftNumber: shiftContext.shiftNumber,
            status: 'fiscalized',
            fiscalData,
            createdAt
          })
          await this.enqueueForCloud({
            receiptId,
            deviceId,
            total: input.total,
            paymentMethod: input.paymentMethod,
            cashierName: input.cashierName,
            settlementType: input.settlementType,
            originalReceiptId,
            items,
            zoneId: shiftContext.zoneId,
            shiftNumber: shiftContext.shiftNumber,
            kktShiftNumber: fiscalData.shiftNumber,
            status: 'fiscalized',
            createdAt
          })
          return { ok: true, receiptId, status: 'fiscalized', fiscalData }
        } catch (err) {
          // Печать не удалась (завис ответ, сбой) — уходим в офлайн-ветку.
          console.warn('[CorrectionService] Печать коррекции не удалась:', err)
        }
      }


      // 2) ККТ недоступна/сбой — откладываем дофискализацию.
      await this.saveReceipt({
        receiptId,
        total: input.total,
        paymentMethod: input.paymentMethod,
        cashierName: input.cashierName,
        settlementType: input.settlementType,
        correction: input.correction,
        originalReceiptId,
        items,
        zoneId: shiftContext.zoneId,
        shiftNumber: shiftContext.shiftNumber,
        status: 'offline_pending',
        createdAt
      })
      await db.offlineQueue.add({
        type: 'fiscalize',
        payload: {
          receiptId,
          total: input.total,
          items: fiscalItems,
          docType: 'correction',
          settlementType: input.settlementType,
          correction: input.correction
        },
        attempts: 0,
        status: 'pending',
        createdAt
      })
      await this.enqueueForCloud({
        receiptId,
        deviceId,
        total: input.total,
        paymentMethod: input.paymentMethod,
        cashierName: input.cashierName,
        settlementType: input.settlementType,
        originalReceiptId,
        items,
        zoneId: shiftContext.zoneId,
        shiftNumber: shiftContext.shiftNumber,
        status: 'offline_pending',
        createdAt
      })
      return {
        ok: true,
        receiptId,
        status: 'offline_pending',
        kktState: fiscalService.getKktStatus()?.state
      }
    } catch (err) {
      return {
        ok: false,
        receiptId,
        status: 'draft',
        error: err instanceof Error ? err.message : String(err)
      }
    }
  }

  /** Последние чеки коррекции (только чтение — редактирование запрещено). */
  public async listCorrections(limit = 20): Promise<SaleReceipt[]> {
    const all = await db.receipts.filter((r) => r.docType === 'correction').toArray()
    return all.sort((a, b) => b.createdAt - a.createdAt).slice(0, limit)
  }

  /**
   * Сумма уже оформленных коррекций-возвратов по исходному чеку (65.6).
   * Нужна для лимита: коррекция суммовая, позиций у неё нет.
   */
  public async correctionReturnsTotal(originalReceiptId: string): Promise<number> {
    const corrections = await db.receipts
      .where('originalReceiptId')
      .equals(originalReceiptId)
      .toArray()
    return corrections
      .filter(
        (r) =>
          r.docType === 'correction' && (r.settlementType ?? 'income') === 'income_return'
      )
      .reduce((sum, r) => sum + Math.abs(r.total), 0)
  }

  /**
   * Позиции уже оформленных коррекций-возвратов по чеку (65.6.2).
   * Лимит по количеству: состав новой коррекции не больше остатка.
   * У коррекций, созданных до 65.6.2, состава нет — они учтены только суммой.
   */
  public async correctionReturnedItems(originalReceiptId: string): Promise<SaleReceiptItem[]> {
    const corrections = await db.receipts
      .where('originalReceiptId')
      .equals(originalReceiptId)
      .toArray()
    return corrections
      .filter(
        (r) =>
          r.docType === 'correction' && (r.settlementType ?? 'income') === 'income_return'
      )
      .flatMap((r) => r.items ?? [])
  }

  /** Сохранить локальный чек коррекции. */
  private async saveReceipt(data: {
    receiptId: string
    total: number
    paymentMethod: string
    cashierName?: string
    settlementType: SettlementType
    correction: CorrectionInfo
    originalReceiptId?: string
    items?: CorrectionLine[]
    zoneId?: string
    shiftNumber?: number
    status: ReceiptStatus
    fiscalData?: FiscalReceiptData
    createdAt: number
  }): Promise<void> {
    await db.receipts.add({
      receiptId: data.receiptId,
      items: (data.items ?? []).map((i) => ({
        barcode: i.barcode ?? '',
        name: i.name,
        qty: i.qty,
        price: i.price,
        unit: i.unit
      })),
      total: data.total,
      status: data.status,
      paymentMethod: data.paymentMethod,
      cashierName: data.cashierName,
      docType: 'correction',
      settlementType: data.settlementType,
      correction: data.correction,
      originalReceiptId: data.originalReceiptId,
      zoneId: data.zoneId,
      shiftNumber: data.shiftNumber,
      fiscalData: data.fiscalData,
      createdAt: data.createdAt
    })
  }

  /** Поставить коррекцию в облачную очередь отправки (sales_queue). */
  private async enqueueForCloud(data: {
    receiptId: string
    deviceId: string
    total: number
    paymentMethod: string
    cashierName?: string
    settlementType: SettlementType
    originalReceiptId?: string
    items?: CorrectionLine[]
    zoneId?: string
    shiftNumber?: number
    kktShiftNumber?: number
    status: ReceiptStatus
    createdAt: number
  }): Promise<void> {
    const sale: QueuedSale = {
      id: data.receiptId,
      device_id: data.deviceId,
      total_amount: data.total,
      items: (data.items ?? []).map((i) => ({
        barcode: i.barcode ?? '',
        name: i.name,
        quantity: i.qty,
        price: i.price,
        unit: i.unit
      })),
      sold_at: new Date(data.createdAt).toISOString(),
      is_synced: false,
      payment_method: data.paymentMethod,
      settlement_type: data.settlementType,
      original_receipt_id: data.originalReceiptId,
      zone_id: data.zoneId,
      shift_number: data.kktShiftNumber ?? data.shiftNumber,
      cashier_name: data.cashierName,
      status: data.status,
      doc_type: 'correction'
    }
    await db.sales_queue.put(sale)
  }
}

export const correctionService = new CorrectionService()

